/**
 * Página de pago por PaymentIntent (producto por slug).
 * Ruta: /pagar/:paymentIntentId
 */

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import { STRIPE_PUBLISHABLE_KEY, STRIPE_APPEARANCE } from "@/config/stripe";
import { Loader2 } from "lucide-react";

let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

const API_BASE =
  typeof window !== "undefined"
    ? `${window.location.origin}/api`
    : "/api";

export default function PagarPage() {
  const { t, i18n } = useTranslation();
  const { paymentIntentId } = useParams<{ paymentIntentId: string }>();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stripeFormLang = useMemo(() => {
    const base = (i18n.language || "es").split("-")[0]?.toLowerCase() ?? "es";
    return base === "es" ? "es" : "en";
  }, [i18n.language]);

  useEffect(() => {
    if (!paymentIntentId || !paymentIntentId.startsWith("pi_")) {
      setError(t("pagarPage.invalidId"));
      setLoading(false);
      return;
    }

    const url = `${API_BASE}/products/payment-intent-client-secret?paymentIntentId=${encodeURIComponent(paymentIntentId)}`;
    fetch(url)
      .then((res) => {
        if (!res.ok)
          return res
            .json()
            .then((data) =>
              Promise.reject(new Error(data?.error || t("pagarPage.loadError")))
            );
        return res.json();
      })
      .then((data: { clientSecret: string; amountCents?: number | null }) => {
        setClientSecret(data.clientSecret);
        setAmountCents(data.amountCents ?? null);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : t("pagarPage.genericError"))
      )
      .finally(() => setLoading(false));
  }, [paymentIntentId, t]);

  const handleSuccess = (id?: string) => {
    const params = new URLSearchParams();
    if (id) params.set("payment_intent", id);
    navigate(`/gracias?${params.toString()}`);
  };

  const handleBack = () => navigate(-1);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/60 text-sm">{t("pagarPage.preparing")}</p>
        </div>
      </div>
    );
  }

  if (error || !clientSecret) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center space-y-4">
          <h2 className="text-xl font-semibold text-white">{t("pagarPage.errorTitle")}</h2>
          <p className="text-white/60 text-sm">{error || t("pagarPage.couldNotLoad")}</p>
          <button
            type="button"
            onClick={handleBack}
            className="w-full bg-white/10 text-white font-semibold py-3 px-6 rounded-xl hover:bg-white/20"
          >
            {t("pagarPage.back")}
          </button>
        </div>
      </div>
    );
  }

  const stripePromiseInstance = getStripe();
  if (!stripePromiseInstance) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/60">{t("pagarPage.stripeNotConfigured")}</p>
      </div>
    );
  }

  return (
    <div
      className="h-dvh flex flex-col bg-black"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Elements
        stripe={stripePromiseInstance}
        options={{
          clientSecret,
          appearance: STRIPE_APPEARANCE,
          locale: stripeFormLang === "es" ? "es" : "en",
        }}
      >
        <div className="flex-1 flex flex-col min-h-0">
          <StripeCheckoutForm
            amount={amountCents ?? 0}
            currency="usd"
            packName={t("pagarPage.productLabel")}
            language={stripeFormLang}
            onSuccess={handleSuccess}
            onError={setError}
            onBack={handleBack}
          />
        </div>
      </Elements>
    </div>
  );
}
